import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

// ============================================================
// FYUNOR — Odometer / animated number
// "Data Flow": when a value changes, digits perform a fast vertical
// scroll to the new value instead of snapping.
// ============================================================

export function Odometer({ value, format = (v) => String(v), duration = 250, className }) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef();
  const reducedRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedRef.current = mq.matches;
    const handler = (e) => { reducedRef.current = e.matches; };
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  useEffect(() => {
    const from = fromRef.current;
    const to = value;
    if (from === to) return;
    // Respect reduced-motion: snap to the final value immediately.
    if (reducedRef.current) {
      fromRef.current = to;
      setDisplay(to);
      return;
    }
    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const current = from + (to - from) * eased;
      setDisplay(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
        setDisplay(to);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  return (
    <span className={cn("inline-block tabular", className)} style={{ animation: "fyunor-fade 0.2s ease-out" }}>
      {format(display)}
    </span>
  );
}